import { h, formatTime, formatDuration } from '../ui/dom.js';
import {
  card,
  button,
  emptyState,
  metric,
  progressBar,
  badge,
  toast,
} from '../ui/components.js';
import { api, ApiError } from '../api.js';
import { can } from '../store.js';
import type { Page, PageContext } from '../page.js';
import { READINESS_LABELS, type ChecklistItem, type ChecklistView, type ReadinessStatus } from '../types.js';

const COLUMN_ORDER: ReadinessStatus[] = ['PENDENTE', 'EM_PREPARACAO', 'HOMOLOGADO'];

export async function checklistPage(context: PageContext): Promise<Page> {
  const { id } = context.params;
  if (!id) return { title: 'Prontidão', body: emptyState('Edição não informada.') };

  const view = await api.get<ChecklistView>(`/api/editions/${id}/checklist`);
  const canManage = can.createEditions();
  const totalDays = view.schedule.days || (view.edition.days ?? []).length || 1;

  const moveTo = async (item: ChecklistItem, readiness: ReadinessStatus): Promise<void> => {
    try {
      await api.post(`/api/ministrations/${item.id}/readiness`, { readiness });
      toast.success('Status atualizado', `${item.title} · ${READINESS_LABELS[readiness]}`);
      window.location.reload();
    } catch (error) {
      toast.error('Falha ao atualizar', error instanceof ApiError ? error.message : undefined);
    }
  };

  const columns = COLUMN_ORDER.map((status) => {
    const items = view.columns[status] ?? [];

    const list = h('div', { class: 'kanban__list' });
    if (items.length === 0) {
      list.append(h('div', { class: 'kanban__empty', text: 'Nenhuma ministração nesta etapa' }));
    }

    for (const item of items) {
      const variant =
        status === 'HOMOLOGADO' ? 'success' : status === 'EM_PREPARACAO' ? 'warning' : '';
      const tone = item.overrunRisk ? 'danger' : variant;

      list.append(
        h(
          'div',
          { class: `kanban-card${tone ? ` kanban-card--${tone}` : ''}` },
          h('div', { class: 'kanban-card__title', text: item.title }),
          h(
            'div',
            { class: 'row small muted' },
            h('span', { class: 'mono', text: formatTime(item.scheduledStart) }),
            h('span', { text: '·' }),
            h('span', { text: item.ministradorName ?? 'Sem ministrador' }),
          ),
          h(
            'div',
            { class: 'row' },
            totalDays > 1 ? badge(`Dia ${item.dayNumber}`, 'accent') : null,
            item.mediaCount > 0
              ? badge(`${item.mediaCount} material(is)`, 'info')
              : badge('Sem material', 'neutral'),
            item.hasScript ? badge('Roteiro próprio', 'accent') : null,
            item.overrunRisk ? badge('Atrasado', 'danger') : null,
          ),
          canManage
            ? h(
                'div',
                { class: 'row', style: { marginTop: '4px' } },
                ...COLUMN_ORDER.filter((target) => target !== status).map((target) =>
                  button(shortLabel(target), {
                    size: 'sm',
                    variant: 'ghost',
                    onClick: () => void moveTo(item, target),
                  }),
                ),
              )
            : null,
        ),
      );
    }

    return h(
      'div',
      { class: 'kanban__column' },
      h(
        'div',
        { class: 'kanban__head' },
        h('span', { text: READINESS_LABELS[status] }),
        h('span', { class: 'kanban__count', text: String(items.length) }),
      ),
      list,
    );
  });

  return {
    title: 'Check-list de Prontidão',
    subtitle: `${view.edition.name} · pipeline de homologação das ministrações`,
    actions: [
      button('Voltar à edição', {
        variant: 'ghost',
        size: 'sm',
        onClick: () => context.navigate(`/edicoes/${id}`),
      }),
      button('Painel de apoio', {
        variant: 'subtle',
        size: 'sm',
        onClick: () => context.navigate(`/edicoes/${id}/apoio`),
      }),
    ],
    body: [
      h(
        'div',
        { class: 'grid grid--4' },
        metric('Total', view.totals.total, 'accent'),
        metric('Pendentes', view.totals.pendente, view.totals.pendente > 0 ? 'warning' : 'default'),
        metric('Em preparação', view.totals.emPreparacao, 'info'),
        metric('Homologadas', view.totals.homologado, 'success'),
      ),
      card({
        title: 'Progresso da homologação',
        hint: `${totalDays} dia(s) de encontro · grade total de ${formatDuration(view.schedule.totalMinutes)}`,
        body: h(
          'div',
          { class: 'stack' },
          h(
            'div',
            { class: 'row row--between' },
            h('div', { class: 'muted small', text: `${view.totals.progressPercent}% concluído` }),
            h('div', {
              class: 'muted small',
              text: `${view.totals.homologado} de ${view.totals.total} ministrações`,
            }),
          ),
          progressBar(view.totals.progressPercent),
        ),
      }),
      canManage
        ? null
        : h('div', {
            class: 'muted small',
            text: 'Seu perfil acompanha o painel em modo somente leitura.',
          }),
      h('div', { class: 'kanban' }, ...columns),
    ],
  };
}

function shortLabel(status: ReadinessStatus): string {
  switch (status) {
    case 'PENDENTE':
      return '→ Pendente';
    case 'EM_PREPARACAO':
      return '→ Em preparação';
    default:
      return '→ Homologar';
  }
}
